import { useAuth } from '../../context/AuthContext';
import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, Check, Package } from 'lucide-react';

const CATEGORIES = ['Cils', 'Sourcils', 'Regard', 'Visage', 'Corps', 'Ongles', 'Épilation', 'Massages', 'Hair'];

export default function AdminAddonProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nom: '', description: '', prix: '', image_url: '', categories: [], actif: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const { getAuthHeaders } = useAuth();
  const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };

  const load = () => {
    setLoading(true);
    fetch(`/api/admin/addon-products`, { headers })
      .then(r => r.json()).then(setProducts).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ nom: '', description: '', prix: '', image_url: '', categories: [], actif: true });
    setError('');
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ nom: p.nom, description: p.description || '', prix: p.prix, image_url: p.image_url || '', categories: p.categories || [], actif: p.actif });
    setError('');
    setShowForm(true);
  };

  const toggleCat = (cat) => {
    setForm(f => ({
      ...f,
      categories: f.categories.includes(cat) ? f.categories.filter(c => c !== cat) : [...f.categories, cat]
    }));
  };

  const save = async () => {
    if (!form.nom || !form.prix || form.categories.length === 0) {
      setError('Nom, prix et au moins une catégorie sont requis.');
      return;
    }
    setSaving(true); setError('');
    const url = editing ? `/api/admin/addon-products/${editing}` : `/api/admin/addon-products`;
    const method = editing ? 'PUT' : 'POST';
    const r = await fetch(url, { method, headers, body: JSON.stringify({ ...form, prix: parseFloat(form.prix) }) });
    setSaving(false);
    if (r.ok) { setShowForm(false); load(); }
    else { const d = await r.json(); setError(d.detail || 'Erreur'); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    await fetch(`/api/admin/addon-products/${id}`, { method: 'DELETE', headers });
    load();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setForm(prev => ({ ...prev, image_url: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif">Produits additionnels</h1>
          <p className="text-sm text-muted-foreground mt-1">Produits proposés en upsell lors de la réservation</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 text-sm rounded-sm font-semibold" style={{background:'#3d1f2a',color:'#f0dde4'}}>
          <Plus size={16} /> Ajouter
        </button>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground py-12">Chargement…</p>
      ) : products.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-sm">
          <Package size={40} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">Aucun produit pour l'instant</p>
          <button onClick={openCreate} className="mt-4 text-sm underline text-accent">Ajouter le premier produit</button>
        </div>
      ) : (
        <div className="space-y-3">
          {products.map(p => (
            <div key={p.id} className={`flex items-center gap-4 p-4 border rounded-sm ${p.actif ? 'border-border' : 'border-border opacity-50'}`}>
              {p.image_url ? (
                <img src={p.image_url} alt={p.nom} className="w-14 h-14 object-cover rounded-sm flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 bg-secondary rounded-sm flex items-center justify-center flex-shrink-0">
                  <Package size={20} className="text-accent" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium">{p.nom}</p>
                  {!p.actif && <span className="text-xs bg-secondary px-2 py-0.5 rounded-full">Inactif</span>}
                </div>
                {p.description && <p className="text-xs text-muted-foreground mt-0.5 truncate">{p.description}</p>}
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-sm font-semibold text-accent">{parseFloat(p.prix).toFixed(2)} €</span>
                  <span className="text-xs text-muted-foreground">{(p.categories || []).join(', ')}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button onClick={() => openEdit(p)} className="p-2 hover:bg-secondary rounded-sm"><Pencil size={15} /></button>
                <button onClick={() => del(p.id)} className="p-2 hover:bg-red-50 text-red-500 rounded-sm"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(30,10,18,0.55)'}}>
          <div className="bg-white rounded-sm shadow-xl w-full max-w-md overflow-y-auto max-h-screen">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-serif text-lg">{editing ? 'Modifier le produit' : 'Nouveau produit'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-sm">{error}</p>}
              <div>
                <label className="block text-xs font-medium mb-1">Nom *</label>
                <input value={form.nom} onChange={e => setForm(f => ({...f, nom: e.target.value}))} className="w-full border border-input rounded-sm px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} className="w-full border border-input rounded-sm px-3 py-2 text-sm resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Prix (€) *</label>
                <input type="number" step="0.01" min="0" value={form.prix} onChange={e => setForm(f => ({...f, prix: e.target.value}))} className="w-full border border-input rounded-sm px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Image</label>
                <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm" />
                {form.image_url && <img src={form.image_url} alt="Aperçu" className="mt-2 h-20 w-20 object-cover rounded border" />}
              </div>
              <div>
                <label className="block text-xs font-medium mb-2">Catégories de prestations *</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map(cat => (
                    <button key={cat} type="button" onClick={() => toggleCat(cat)}
                      className={`px-3 py-1 text-xs rounded-full border transition-all ${form.categories.includes(cat) ? 'bg-[#3d1f2a] text-white border-[#3d1f2a]' : 'border-border hover:border-accent'}`}>
                      {form.categories.includes(cat) && <Check size={10} className="inline mr-1" />}{cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="actif" checked={form.actif} onChange={e => setForm(f => ({...f, actif: e.target.checked}))} className="rounded" />
                <label htmlFor="actif" className="text-sm">Produit actif (visible lors des réservations)</label>
              </div>
            </div>
            <div className="px-6 pb-6">
              <button onClick={save} disabled={saving} className="w-full py-3 text-sm font-semibold tracking-widest uppercase rounded-sm" style={{background:'#3d1f2a',color:'#f0dde4'}}>
                {saving ? 'Enregistrement…' : editing ? 'Enregistrer' : 'Créer le produit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
